/**
 * Renew Certificate Page
 *
 * Mobile-first page for uploading a new digital certificate
 * to replace an expired one.
 */

import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import {
  Upload,
  FileKey,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle2,
  ChevronLeft,
  Shield,
  Loader2,
  Calendar,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/toaster'
import { useTenant } from '@/hooks/useTenant'
import { useTranslation } from '@/hooks/useTranslation'
import { TribuCrGuide } from '@/components/settings/TribuCrGuide'
import { cn } from '@/lib/utils'
import api from '@/lib/api'

interface RenewResponse {
  success: boolean
  certificate_status?: string
  certificate_expiry?: string
  certificate_serial?: string
  error?: string
}

export function RenewCertificatePage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { toast } = useToast()
  const { tenant, refreshTenant } = useTenant()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<File | null>(null)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [dragActive, setDragActive] = useState(false)

  // Renew certificate mutation
  const renewMutation = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error(t('settings.renewKey.noFileSelected'))
      }

      const formData = new FormData()
      formData.append('certificate', file)
      formData.append('certificate_password', password)

      const response = await api.post('/tenants/renew-certificate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data as RenewResponse
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: t('settings.renewKey.renewed'),
          description: t('settings.renewKey.renewedDescription'),
        })
        refreshTenant()
        navigate('/settings')
      } else {
        toast({
          title: t('common.error'),
          description: data.error || t('settings.renewKey.renewFailed'),
          variant: 'destructive',
        })
      }
    },
    onError: (error: Error) => {
      toast({
        title: t('common.error'),
        description: error.message || t('settings.renewKey.renewFailed'),
        variant: 'destructive',
      })
    },
  })

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile && (droppedFile.name.endsWith('.p12') || droppedFile.name.endsWith('.pfx'))) {
      setFile(droppedFile)
    } else {
      toast({
        title: t('settings.renewKey.invalidFile'),
        description: t('settings.renewKey.invalidFileDescription'),
        variant: 'destructive',
      })
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile) {
      setFile(selectedFile)
    }
  }

  const handleSubmit = () => {
    if (!file) {
      toast({
        title: t('common.error'),
        description: t('settings.renewKey.selectFileFirst'),
        variant: 'destructive',
      })
      return
    }

    if (!password) {
      toast({
        title: t('common.error'),
        description: t('settings.renewKey.enterPin'),
        variant: 'destructive',
      })
      return
    }

    renewMutation.mutate()
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return t('settings.renewKey.notAvailable')
    try {
      return new Date(dateString).toLocaleDateString('es-CR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const isExpired = tenant?.certificate_is_expired
  const isExpiringSoon = tenant?.certificate_days_until_expiry !== undefined &&
    tenant.certificate_days_until_expiry <= 30 &&
    tenant.certificate_days_until_expiry > 0

  return (
    <div className="flex flex-col min-h-[calc(100dvh-4rem)]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b px-4 py-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="shrink-0"
            aria-label={t('common.back')}
          >
            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold flex items-center gap-2">
              <FileKey className="h-5 w-5 shrink-0" />
              <span className="truncate">{t('settings.renewKey.title')}</span>
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('settings.renewKey.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Current certificate status */}
        {tenant && (
          <div className={cn(
            'p-4 rounded-lg border',
            isExpired
              ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950'
              : isExpiringSoon
                ? 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950'
                : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
          )}>
            <div className="flex items-start gap-3">
              {isExpired ? (
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              ) : isExpiringSoon ? (
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
              ) : (
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
              )}
              <div className="flex-1 min-w-0 space-y-2">
                <p className="font-medium">
                  {isExpired
                    ? t('settings.renewKey.statusExpired')
                    : isExpiringSoon
                      ? t('settings.renewKey.statusExpiringSoon')
                      : t('settings.renewKey.statusValid')}
                </p>

                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{t('settings.renewKey.expiresOn', { date: formatDate(tenant.certificate_expiry) })}</span>
                  </div>
                  {tenant.certificate_days_until_expiry !== undefined && (
                    <Badge variant={isExpired ? 'destructive' : isExpiringSoon ? 'secondary' : 'outline'}>
                      {isExpired
                        ? t('settings.renewKey.expiredDaysAgo', { days: Math.abs(tenant.certificate_days_until_expiry) })
                        : t('settings.renewKey.daysLeft', { days: tenant.certificate_days_until_expiry })}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <p className="text-sm text-muted-foreground">{t('settings.renewKey.intro')}</p>

        <TribuCrGuide topic="key" isSandbox={tenant?.alanube_environment === 'sandbox'} />

        {/* File upload area */}
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-6 text-center transition-colors min-h-[160px] flex items-center justify-center',
            dragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25',
            file && 'border-green-500 bg-green-50 dark:bg-green-950'
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !file && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".p12,.pfx"
            onChange={handleFileChange}
            className="hidden"
          />

          {file ? (
            <div className="space-y-3">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
              <p className="font-medium text-green-700 dark:text-green-300 truncate max-w-full mx-auto">
                {file.name}
              </p>
              <p className="text-sm text-muted-foreground">
                {(file.size / 1024).toFixed(1)} KB
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setFile(null)
                }}
              >
                {t('settings.renewKey.changeFile')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
              <p className="text-sm text-muted-foreground hidden sm:block">
                {t('settings.renewKey.dropHere')}
              </p>
              <Button
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef.current?.click()
                }}
              >
                {t('settings.renewKey.selectFile')}
              </Button>
              <p className="text-xs text-muted-foreground">
                {t('settings.renewKey.acceptedFormats')}
              </p>
            </div>
          )}
        </div>

        {/* Password input */}
        <div className="space-y-2">
          <Label htmlFor="cert-password">{t('settings.registration.keyPin')}</Label>
          <div className="relative">
            <Input
              id="cert-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="one-time-code"
              placeholder={t('settings.registration.keyPinPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 pr-12"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-12 w-12"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('settings.renewKey.hidePin') : t('settings.renewKey.showPin')}
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" />
              ) : (
                <Eye className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Warning */}
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertTitle>{t('settings.renewKey.importantTitle')}</AlertTitle>
          <AlertDescription>{t('settings.renewKey.importantHint')}</AlertDescription>
        </Alert>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 z-10 bg-background border-t p-4 space-y-2">
        <Button
          onClick={handleSubmit}
          disabled={!file || !password || renewMutation.isPending}
          className="w-full h-12"
        >
          {renewMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('settings.renewKey.uploading')}
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              {t('settings.renewKey.upload')}
            </>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/settings')}
          disabled={renewMutation.isPending}
          className="w-full h-12"
        >
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}

export { RenewCertificatePage as default }
